// BANK1 (docs/build-briefs/BANK1-bank-accounts-in-app.md; DEC-509). The pure rules behind the "Bank accounts" card in Settings
// and the same step in the setup wizard: three rows (Deposits, Royalties, Snip & Sip subscription), each with a status, the
// last 4 when Stripe gave one, and at most one action. Labels and states only (the owner's copy rule); the one sentence shown
// beside an action is the authorization text the server sends and hashes, which the signer must see. No DOM, so it is tested
// directly. Nothing here names an account, a customer or a payment method: the server sends none, and no bank number is typed,
// held or shown here (only "Account ending 1234").

import type { RoyaltyAuthorizationDto, SubscriptionDebitDto } from "./api.ts";
import type { CardPaymentsForLocationDto, LocationCardPaymentsDto } from "./cardPaymentsModel.ts";

export type BankRowId = "deposits" | "royalties" | "subscription";
export type BankStatus = "connected" | "attention" | "not_connected";
export type BankActionKind = "connect" | "change" | "fix" | "setup";

export interface BankRowView {
  id: BankRowId;
  title: string;
  /** The owner's one line for the row. */
  caption: string;
  status: BankStatus;
  /** Symbol plus words, never color alone. */
  chip: { symbol: string; label: string; tone: "good" | "warn" | "neutral" };
  /** "Account ending 1234", "Waiting on Snip & Sip", or null. */
  detail: string | null;
  /** The one action, or null when there is nothing to press (or the person may not). */
  action: { kind: BankActionKind; label: string } | null;
  /** The person could act but their role may not: "View only" shows beside the chip. */
  viewOnly: boolean;
  /** The authorization text to show before the redirect (Royalties and Subscription), or null. */
  disclosure: string | null;
}

export const BANK_CAPTIONS: Readonly<Record<BankRowId, string>> = {
  deposits: "Where your card sales are paid",
  royalties: "The account your franchisor debits each month",
  subscription: "The account Snip & Sip debits for your monthly plan",
};

export const BANK_TITLES: Readonly<Record<BankRowId, string>> = {
  deposits: "Deposits",
  royalties: "Royalties",
  subscription: "Snip & Sip subscription",
};

const CHIPS: Record<BankStatus, BankRowView["chip"]> = {
  connected: { symbol: "✓", label: "Connected", tone: "good" },
  attention: { symbol: "!", label: "Needs attention", tone: "warn" },
  not_connected: { symbol: "", label: "Not connected", tone: "neutral" },
};

const ACTION_LABELS: Record<BankActionKind, string> = {
  connect: "Connect",
  change: "Change",
  fix: "Fix",
  setup: "Set up deposits",
};

/** "Account ending 1234", or null with no valid last 4. */
export function bankLine(last4: string | null | undefined): string | null {
  return typeof last4 === "string" && /^\d{4}$/.test(last4) ? `Account ending ${last4}` : null;
}

function row(
  id: BankRowId,
  status: BankStatus,
  detail: string | null,
  kind: BankActionKind | null,
  canAct: boolean,
  disclosure: string | null,
): BankRowView {
  const offered = kind !== null;
  return {
    id,
    title: BANK_TITLES[id],
    caption: BANK_CAPTIONS[id],
    status,
    chip: CHIPS[status],
    detail,
    action: offered && canAct ? { kind, label: ACTION_LABELS[kind] } : null,
    viewOnly: offered && !canAct,
    disclosure: offered && canAct ? disclosure : null,
  };
}

// ---------------------------------------------------------------------------------------------------
// Deposits: the payout bank of the location's own Stripe connected account (Stripe's hosted onboarding)
// ---------------------------------------------------------------------------------------------------

/** A tenant never sees what is still the software owner's to do: a set-up under way and a location with no legal business
 * both read as not set up (cardPayments.ts does the same). */
function cardKind(here: LocationCardPaymentsDto): LocationCardPaymentsDto["kind"] {
  return here.kind === "creating" || here.kind === "no_legal_entity" ? "not_set_up" : here.kind;
}

export const WAITING_ON_SNIPSIP = "Waiting on Snip & Sip";

/**
 * Stripe does not return a Standard account's payout bank to the platform, so there is no last 4 here: status only.
 * Set up deposits (no account yet) creates the location's Stripe account and goes straight to Stripe's hosted onboarding. Connect
 * and Fix go to Stripe's hosted onboarding (the existing Account Link). Change, on an account that is already On,
 * opens the owner's own Stripe Dashboard payout settings, because a Standard account's owner changes its bank there.
 */
export function depositsRow(input: {
  card: CardPaymentsForLocationDto | undefined;
  /** The session holds admin.payments.onboard. */
  canOnboard: boolean;
}): BankRowView | undefined {
  if (input.card === undefined) return undefined;
  const rawKind = input.card.thisLocation.kind;
  const kind = rawKind === "no_legal_entity" ? rawKind : cardKind(input.card.thisLocation);
  const pending = input.card.thisLocation.requirements.some((r) => r.pending);
  switch (kind) {
    case "enabled":
      return row("deposits", "connected", null, "change", input.canOnboard, null);
    case "restricted":
      return row("deposits", "attention", null, pending ? "fix" : null, input.canOnboard, null);
    case "disconnected":
      return row("deposits", "attention", null, null, input.canOnboard, null);
    case "not_started":
    case "in_progress":
      return row("deposits", "not_connected", null, "connect", input.canOnboard, null);
    case "not_set_up":
    case "creating":
      // DEC-509 (self-serve deposits): with no connected account the Admin starts the set-up from here.
      return row("deposits", "not_connected", null, "setup", input.canOnboard, null);
    case "no_legal_entity":
      return row("deposits", "not_connected", WAITING_ON_SNIPSIP, null, input.canOnboard, null);
  }
}

/** Where Change goes for a Connected deposit account: the owner's Stripe Dashboard payout settings, an https Stripe address. */
export function payoutSettingsUrl(mode: "test" | "live" | undefined): string {
  return mode === "live"
    ? "https://dashboard.stripe.com/settings/payouts"
    : "https://dashboard.stripe.com/test/settings/payouts";
}

// ---------------------------------------------------------------------------------------------------
// Royalties and Snip & Sip subscription: an ACH debit authorization on Stripe's own page
// ---------------------------------------------------------------------------------------------------

function authorizationRow(
  id: "royalties" | "subscription",
  dto: RoyaltyAuthorizationDto | SubscriptionDebitDto | undefined,
  canSign: boolean,
): BankRowView | undefined {
  // The server decides whether a row is offered at all; nothing is drawn, and nothing said, for anything else.
  if (dto?.applies !== true) return undefined;
  const bank = bankLine(dto.bankLast4);
  const text = dto.mandate?.text ?? null;
  if (dto.status === "active") return row(id, "connected", bank, "change", canSign, text);
  if (dto.status === "needs_reauthorization") return row(id, "attention", bank, "fix", canSign, text);
  return row(id, "not_connected", null, "connect", canSign, text);
}

export function royaltiesRow(input: {
  dto: RoyaltyAuthorizationDto | undefined;
  canSign: boolean;
}): BankRowView | undefined {
  return authorizationRow("royalties", input.dto, input.canSign);
}

export function subscriptionRow(input: {
  dto: SubscriptionDebitDto | undefined;
  canSign: boolean;
}): BankRowView | undefined {
  return authorizationRow("subscription", input.dto, input.canSign);
}

// ---------------------------------------------------------------------------------------------------
// The card and the wizard step
// ---------------------------------------------------------------------------------------------------

export interface BankAccountsReads {
  card: CardPaymentsForLocationDto | undefined;
  royalty: RoyaltyAuthorizationDto | undefined;
  subscription: SubscriptionDebitDto | undefined;
  canOnboard: boolean;
}

export interface BankAccountsView {
  rows: BankRowView[];
  /** At least one row is offered. The card and the wizard step show nothing otherwise. */
  offered: boolean;
  /** Every offered row reads Connected. */
  allConnected: boolean;
  /** Every offered debit authorization (Royalties, Snip & Sip subscription) reads Connected. Deposits is finished in the
   * Card payments step, which Snip & Sip may still be preparing, so it never holds the Bank accounts step open. */
  debitsConnected: boolean;
}

export function bankAccountsView(reads: BankAccountsReads): BankAccountsView {
  const rows = [
    depositsRow({ card: reads.card, canOnboard: reads.canOnboard }),
    royaltiesRow({ dto: reads.royalty, canSign: reads.canOnboard }),
    subscriptionRow({ dto: reads.subscription, canSign: reads.canOnboard }),
  ].filter((r): r is BankRowView => r !== undefined);
  return {
    rows,
    offered: rows.length > 0,
    allConnected: rows.length > 0 && rows.every((r) => r.status === "connected"),
    debitsConnected: rows
      .filter((r) => r.id !== "deposits")
      .every((r) => r.status === "connected"),
  };
}

// ---------------------------------------------------------------------------------------------------
// Coming back from Stripe's hosted bank debit page for the subscription
// ---------------------------------------------------------------------------------------------------

/** The marker Stripe's success and cancel addresses carry on the app root. */
export type SubscriptionDebitArrival = "return" | "cancel";

export function readSubscriptionDebitArrival(search: string): SubscriptionDebitArrival | undefined {
  const value = new URLSearchParams(search).get("subscription_debit");
  return value === "return" || value === "cancel" ? value : undefined;
}

/** The same address with the marker removed (everything else, including other keys and the hash, kept). */
export function withoutSubscriptionDebitArrival(search: string): string {
  const params = new URLSearchParams(search);
  params.delete("subscription_debit");
  const rest = params.toString();
  return rest === "" ? "" : `?${rest}`;
}

/** Set (in sessionStorage, for this tab only) when Connect is tapped from the wizard, so the return reopens the wizard on the
 * Bank accounts step instead of Settings. It carries nothing but the fact. */
export const SUBSCRIPTION_DEBIT_RETURN_MARKER_KEY = "bms-setup-wizard-subscription-debit";

type MarkerStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;

export function setSubscriptionDebitReturnMarker(storage: MarkerStorage): void {
  try {
    storage.setItem(SUBSCRIPTION_DEBIT_RETURN_MARKER_KEY, "1");
  } catch {
    // Storage refused: the return falls back to Settings, which reads the same authorization.
  }
}

export function clearSubscriptionDebitReturnMarker(storage: MarkerStorage): void {
  try {
    storage.removeItem(SUBSCRIPTION_DEBIT_RETURN_MARKER_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** True once for a marker that was set; the marker is gone after this call either way. */
export function takeSubscriptionDebitReturnMarker(storage: MarkerStorage): boolean {
  try {
    const set = storage.getItem(SUBSCRIPTION_DEBIT_RETURN_MARKER_KEY) === "1";
    clearSubscriptionDebitReturnMarker(storage);
    return set;
  } catch {
    return false;
  }
}
